import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DecimalPipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { DateHelperService } from '../../services/date-helper.service';
import { takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { isNaN } from 'lodash-es';

@Component({
  selector: 'formly-week-days-hours-time',
  template: `
    <div class="formly-field-week-time">
      <h3 class="mb-2 font-normal">
        {{ props.label }}
        <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
      </h3>
      <!-- Hidden helper controls (totals, summary, per-day number values) -->
      @for (subField of hiddenFields(); track $index) {
        <formly-field [field]="subField" [formlyAttributes]="subField"></formly-field>
      }

      <!-- Weekday columns: Van/Tot per day, even widths (4 per row, 7 on large screens) -->
      <div class="grid grid-cols-4 lg:grid-cols-7 gap-2 w-full">
        @for (column of dayColumns(); track $index) {
          <div class="min-w-0">
            <h4 class="text-xs font-normal flex items-center justify-between gap-1">
              <span class="truncate">{{ column.field.props.label }}</span>
              <span class="shrink-0 inline-block bg-light rounded-full w-4 h-4 text-center leading-4">
                {{ field.formControl.value?.[column.dayName] | number: '1.0-1' }}</span>
            </h4>
            <div class="flex flex-col gap-1">
              @for (subSubField of column.field.fieldGroup; track $index) {
                <formly-field [field]="subSubField" [formlyAttributes]="subSubField"></formly-field>
              }
            </div>
          </div>
        }
      </div>
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
    </div>
  `,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    DecimalPipe,
    InfoTooltipComponent,
  ],
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldWeekDaysHoursTime extends FieldType implements OnInit {
  private destroy$ = injectDestroy();

  // Visible weekday columns (each a "{day}_time" group with Van/Tot inputs).
  readonly dayColumns = signal<{ field: FormlyFieldConfig; dayName: string }[]>([]);
  // Hidden helper controls (total-hours, summary, per-day numbers) kept in the form but not laid out as columns.
  readonly hiddenFields = signal<FormlyFieldConfig[]>([]);

  constructor() {
    super();
  }

  ngOnInit() {
    const subFields = this.field.fieldGroup ?? [];
    this.dayColumns.set(
      subFields
        .filter(subField => subField.fieldGroup?.length)
        .map(subField => ({ field: subField, dayName: this.toDayName(subField.key as string) }))
    );
    this.hiddenFields.set(subFields.filter(subField => !subField.fieldGroup?.length));

    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.calculateHours(value);
      this.calculateSummeryAndTotalHours(value);
    });
    this.calculateSummeryAndTotalHours(this.formControl.value);
  }

  calculateHours(value) {
    DateHelperService.DAYS.forEach(day => {
      if (
        value[day + '_time'] != undefined &&
        value[day + '_time']['start'] != undefined &&
        value[day + '_time']['end'] != undefined
      ) {
        const startHours = DateHelperService.timeStringToFloat(value[day + '_time']['start']);
        const endHours = DateHelperService.timeStringToFloat(value[day + '_time']['end']);
        if (startHours != undefined && endHours != undefined) {
          let hours = endHours - startHours;
          if (isNaN(hours)) {
            hours = 0;
          }
          if (hours < 0) {
            hours = 24 + hours;
          }
          if (hours != this.field.formControl.get(day)?.value) {
            this.field.formControl.get(day).patchValue(hours, { emitEvent: true });
          }
        }
      } else {
        if (0 !== this.field.formControl.get(day)?.value) {
          this.field.formControl.get(day).patchValue(0, { emitEvent: true });
        }
      }
    });
  }

  toDayName(key) {
    return key.split('_')[0];
  }

  calculateSummeryAndTotalHours(value) {
    let total = 0;
    const summary = [];
    DateHelperService.DAYS.forEach(day => {
      if (value[day] != undefined && !isNaN(Number(value[day]))) {
        total = total + Number(value[day]);
        if (Number(value[day]) !== 0) {
          summary.push(DateHelperService.dayNameToShortDayName(day) + ' ' + value[day]);
        }
      }
    });
    if (total !== this.field.formControl.get('total-hours')?.value) {
      this.field.formControl.get('total-hours').patchValue(total, { emitEvent: true });
    }
    if (summary.join(' | ') !== this.field.formControl.get('summary')?.value) {
      this.field.formControl.get('summary').patchValue(summary.join(' | '), { emitEvent: true });
    }
  }

  protected readonly DateHelperService = DateHelperService;
}
